import { Col, List, Modal, Row, Typography } from "antd";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { formatDate } from "@/v1/components/pages/TaxDeclaration/utils";
import { formatAmount } from "@/v1/utils";
import { splitStreetForPreview } from "./formHelpers";
import { getFseFormKind } from "./fseFormByCompanyType";
import { getKapgPreviewSections } from "./kapgPreviewRows";
import {
  TaxRegistration,
  TaxRegistrationActivityType,
  TaxRegistrationEstimatedBalanceType,
  TaxRegistrationTaxCalculationMethod,
} from "./types";
import {
  PreviewCodeCol,
  PreviewErrorAlert,
  PreviewGroupHeader,
  PreviewListContainer,
  PreviewSectionHeader,
  PreviewValueCol,
} from "./styles";

const { Text } = Typography;

interface TaxRegistrationDocumentPreviewProps {
  taxRegistration: TaxRegistration;
  visible: boolean;
  onCancel: () => void;
  /**
   * Why the draft for this preview could not be built. Shown with the rows
   * rather than instead of them: the operator needs the questionnaire in front
   * of them to find the field the message is about (SMT-1335).
   *
   * The title stays neutral because the message is not always ERiC's: `BaseApi`
   * flattens an ERiC rejection, an expired session, a timeout and a 500 into
   * the same plain `Error`, with no field left to tell them apart. An ERiC
   * answer names itself in its own text, so nothing is lost by not claiming it
   * here, while a timeout titled "ERiC rejected the draft" would send the
   * operator looking for a field that is fine.
   */
  draftError?: string;
}

interface TaxRegistrationPreviewRow {
  code?: string;
  label: string;
  value?: string | number | boolean | null;
}

/** A section title, a repeating-block title, or one Zeile of the KapG form. */
type KapgPreviewItem =
  | { kind: "section"; key: string; title: string }
  | { kind: "group"; key: string; title: string }
  | { kind: "row"; key: string; zeile: string; label: string; value: string };

/*
 * ELSTER takes these amounts as a Ganzzahl and kontax-neric-api
 * (`normalizeIntegerAmount`) rounds with `Math.round` before sending, so the
 * preview shows what is transmitted, also for a draft saved with cents before
 * the form took whole euros (SMT-1416).
 */
const formatPreviewAmount = (value?: number) =>
  typeof value === "number" ? formatAmount(Math.round(value)) : undefined;

const getExpectedProfitCode = (type?: TaxRegistrationActivityType) => {
  if (type === TaxRegistrationActivityType.TRADE) {
    return "102";
  }
  if (type === TaxRegistrationActivityType.FREELANCE) {
    return "103";
  }

  return undefined;
};

const getSmallBusinessCode = (smallBusiness?: boolean) => {
  if (smallBusiness === true) {
    return "127";
  }
  if (smallBusiness === false) {
    return "128";
  }

  return undefined;
};

const getCodeFromOptionText = (value?: string) =>
  value?.match(/^\[(\d+)\]/)?.[1];

export const TaxRegistrationDocumentPreview = ({
  taxRegistration,
  visible,
  onCancel,
  draftError,
}: TaxRegistrationDocumentPreviewProps) => {
  const { t } = useTranslation("TaxRegistration");
  const questions = taxRegistration.questions;
  /* Memoised so the empty default does not re-key the rows on every render. */
  const attachments = useMemo(
    () => taxRegistration.attachments ?? [],
    [taxRegistration.attachments]
  );
  const isKapg = getFseFormKind(taxRegistration.user?.companyType) === "KAPG";
  const usesPrivateAddressAsBusinessAddress =
    questions.activity?.businessAddressSameAsPrivateAddress === true;
  const expectedProfitCode = getExpectedProfitCode(questions.activity?.type);
  const personalStreet = splitStreetForPreview(
    questions.personal?.address?.street
  );
  const businessStreet = splitStreetForPreview(
    questions.activity?.businessAddress?.street
  );
  const field = useCallback((key: string) => String(t(`fields.${key}`)), [t]);
  const option = useCallback((key: string) => String(t(`options.${key}`)), [t]);
  const translate = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      String(t(key, options ?? {})),
    [t]
  );
  const formatBoolean = useCallback(
    (value?: boolean) =>
      typeof value === "boolean"
        ? String(t(value ? "common.yes" : "common.no"))
        : undefined,
    [t]
  );

  /*
   * The Kennzahlen below are the ones of the Einzelunternehmer Datenart. In
   * FsE_KapG the same numbers mean other fields, which is why a KapG record
   * gets its own mapper (kapgPreviewRows.ts) instead of this list.
   */
  const previewRows = useMemo<TaxRegistrationPreviewRow[]>(
    () => [
      {
        code: "78",
        label: field("applyForNewTaxNumber"),
        value: String(t("common.yes")),
      },
      { label: field("federalState"), value: questions.taxNumber?.state },
      {
        label: field("taxOffice"),
        value: [
          questions.taxNumber?.taxOfficeName,
          questions.taxNumber?.taxOfficeCode
            ? `(${questions.taxNumber.taxOfficeCode})`
            : undefined,
        ]
          .filter(Boolean)
          .join(" "),
      },
      {
        code: "2",
        label: field("salutation"),
        value:
          questions.personal?.salutation === "MS"
            ? option("salutationMs")
            : questions.personal?.salutation === "MR"
            ? option("salutationMr")
            : questions.personal?.salutation === "NOT_SPECIFIED"
            ? option("notSpecified")
            : undefined,
      },
      {
        code: "2",
        label: field("firstName"),
        value: questions.personal?.firstName,
      },
      {
        code: "2",
        label: field("lastName"),
        value: questions.personal?.lastName,
      },
      {
        code: "3",
        label: field("dateOfBirth"),
        value: formatDate(questions.personal?.dateOfBirth),
      },
      {
        code: "4",
        label: field("profession"),
        value: questions.activity?.description,
      },
      { code: "5", label: field("taxId"), value: questions.personal?.taxId },
      { label: field("email"), value: taxRegistration.email },
      {
        code: "7",
        label: field("street"),
        value: personalStreet.street,
      },
      {
        code: "8",
        label: field("houseNumber"),
        value: personalStreet.houseNumber,
      },
      {
        code: "9",
        label: field("zip"),
        value: questions.personal?.address?.zip,
      },
      {
        code: "9",
        label: field("city"),
        value: questions.personal?.address?.city,
      },
      {
        code: "47",
        label: field("activityType"),
        value: questions.activity?.type
          ? option(`activityType.${questions.activity.type}`)
          : undefined,
      },
      {
        code: "88",
        label: field("startDate"),
        value: formatDate(questions.activity?.startDate),
      },
      {
        code: "19",
        label: field("description"),
        value: questions.activity?.description,
      },
      {
        code: "80",
        label: field("foundingType"),
        value: option("newFormation"),
      },
      {
        code: "80",
        label: field("foundingDate"),
        value: formatDate(questions.activity?.startDate),
      },
      {
        code: usesPrivateAddressAsBusinessAddress ? "56" : undefined,
        label: usesPrivateAddressAsBusinessAddress
          ? field("businessAddressSameAsPrivateAddress")
          : field("businessAddress"),
        value: usesPrivateAddressAsBusinessAddress
          ? formatBoolean(true)
          : questions.activity?.businessAddress
          ? option("differentAddress")
          : undefined,
      },
      ...(!usesPrivateAddressAsBusinessAddress
        ? [
            {
              code: "57",
              label: field("businessStreet"),
              value: businessStreet.street,
            },
            {
              code: "58",
              label: field("businessHouseNumber"),
              value: businessStreet.houseNumber,
            },
            {
              code: "59",
              label: field("businessZip"),
              value: questions.activity?.businessAddress?.zip,
            },
            {
              code: "59",
              label: field("businessCity"),
              value: questions.activity?.businessAddress?.city,
            },
          ]
        : []),
      {
        code: "114",
        label: field("profitDeterminationMethod"),
        value: option("cashBasisAccounting"),
      },
      {
        code: expectedProfitCode,
        label: field("expectedCurrentYear"),
        value: formatPreviewAmount(questions.profit?.expectedCurrentYear),
      },
      {
        code: expectedProfitCode,
        label: field("expectedFollowingYear"),
        value: formatPreviewAmount(questions.profit?.expectedFollowingYear),
      },
      {
        code: "125",
        label: field("expectedTurnover"),
        value: formatPreviewAmount(questions.vat?.expectedTurnover),
      },
      {
        code: "126",
        label: field("expectedTurnoverFollowingYear"),
        value: formatPreviewAmount(
          questions.vat?.expectedTurnoverFollowingYear
        ),
      },
      {
        code: getSmallBusinessCode(questions.vat?.smallBusiness),
        label: field("smallBusiness"),
        value: formatBoolean(questions.vat?.smallBusiness),
      },
      {
        code: questions.vat?.vatIdRequested === true ? "145" : undefined,
        label: field("vatIdRequested"),
        value: formatBoolean(questions.vat?.vatIdRequested),
      },
      {
        code: "129",
        label: field("estimatedBalanceType"),
        value:
          questions.vat?.estimatedBalanceType ===
          TaxRegistrationEstimatedBalanceType.PAYMENT
            ? option("vatPayment")
            : questions.vat?.estimatedBalanceType ===
              TaxRegistrationEstimatedBalanceType.REFUND
            ? option("vatRefund")
            : undefined,
      },
      {
        code: "129",
        label: field("estimatedBalanceAmount"),
        value: formatPreviewAmount(questions.vat?.estimatedBalanceAmount),
      },
      {
        code: "141",
        label: field("taxCalculationMethod"),
        value:
          questions.vat?.taxCalculationMethod ===
          TaxRegistrationTaxCalculationMethod.CASH
            ? option("cash")
            : questions.vat?.taxCalculationMethod ===
              TaxRegistrationTaxCalculationMethod.ACCRUAL
            ? option("accrual")
            : undefined,
      },
      {
        code: getCodeFromOptionText(
          questions.vat?.cashAccountingReason
            ? option(
                `cashAccountingReason.${questions.vat.cashAccountingReason}`
              )
            : undefined
        ),
        label: field("cashAccountingReason"),
        value: questions.vat?.cashAccountingReason
          ? option(`cashAccountingReason.${questions.vat.cashAccountingReason}`)
          : undefined,
      },
    ],
    [
      expectedProfitCode,
      field,
      formatBoolean,
      option,
      businessStreet.houseNumber,
      businessStreet.street,
      personalStreet.houseNumber,
      personalStreet.street,
      questions,
      t,
      taxRegistration.email,
      usesPrivateAddressAsBusinessAddress,
    ]
  );

  /*
   * The sections are flattened into one list so the Zeile column stays in the
   * same place from the first row to the last. Nested lists would restart the
   * grid in every section and the numbers would no longer line up.
   */
  const kapgItems = useMemo<KapgPreviewItem[]>(() => {
    if (!isKapg) return [];

    return getKapgPreviewSections(questions, translate, attachments).flatMap(
      ({ title, rows }) => {
        let currentGroup: string | undefined;

        return [
          { kind: "section" as const, key: `section-${title}`, title },
          ...rows.flatMap((row, index) => {
            const groupItem =
              row.group && row.group !== currentGroup
                ? [
                    {
                      kind: "group" as const,
                      key: `group-${title}-${row.group}`,
                      title: row.group,
                    },
                  ]
                : [];
            currentGroup = row.group;

            return [
              ...groupItem,
              {
                kind: "row" as const,
                key: `row-${title}-${row.group ?? ""}-${index}`,
                zeile: row.zeile,
                label: row.label,
                value: row.value,
              },
            ];
          }),
        ];
      }
    );
  }, [attachments, isKapg, questions, translate]);

  const listHeader = (
    <Row>
      <PreviewCodeCol span={4}>
        <Text strong>
          {String(t(isKapg ? "preview.columns.line" : "preview.columns.code"))}
        </Text>
      </PreviewCodeCol>
      <Col span={12}>
        <Text strong>{String(t("preview.columns.description"))}</Text>
      </Col>
      <PreviewValueCol span={8}>
        <Text strong>{String(t("preview.columns.value"))}</Text>
      </PreviewValueCol>
    </Row>
  );

  return (
    <Modal
      footer={null}
      onCancel={onCancel}
      title={String(t("preview.title"))}
      visible={visible}
      width={900}
    >
      {draftError && (
        <PreviewErrorAlert
          description={draftError}
          message={String(t("preview.draftFailed"))}
          showIcon
          type="error"
        />
      )}
      <PreviewListContainer>
        {isKapg ? (
          <List
            dataSource={kapgItems}
            header={listHeader}
            renderItem={(item) => (
              <List.Item>
                {item.kind === "section" ? (
                  <PreviewSectionHeader>{item.title}</PreviewSectionHeader>
                ) : item.kind === "group" ? (
                  <PreviewGroupHeader>{item.title}</PreviewGroupHeader>
                ) : (
                  <Row style={{ width: "100%" }}>
                    <PreviewCodeCol span={4}>{item.zeile}</PreviewCodeCol>
                    <Col span={12}>{item.label}</Col>
                    <PreviewValueCol span={8}>{item.value}</PreviewValueCol>
                  </Row>
                )}
              </List.Item>
            )}
            rowKey={({ key }) => key}
          />
        ) : (
          <List
            dataSource={previewRows.filter(
              ({ value }) =>
                value !== undefined && value !== null && value !== ""
            )}
            header={listHeader}
            renderItem={({ code, label, value }) => (
              <List.Item>
                <Row style={{ width: "100%" }}>
                  <PreviewCodeCol span={4}>{code}</PreviewCodeCol>
                  <Col span={12}>{label}</Col>
                  <PreviewValueCol span={8}>{String(value)}</PreviewValueCol>
                </Row>
              </List.Item>
            )}
          />
        )}
      </PreviewListContainer>
    </Modal>
  );
};
